import { inject, injectable } from 'inversify'
import { computed, makeObservable } from 'mobx'
import { UserModel } from '@core/infra/authentication/user.model'
import { AuthenticationRepository } from '@core/infra/authentication/authentication.repository'
import { I18nRepository } from '@core/infra/i18n/i18n.repository'

/**
 * The top-bar workspace switcher (plan slice 6). Holds no state of its own —
 * it reads UserModel.memberships / .activeWorkspace and delegates the switch to
 * AuthenticationRepository, which re-fetches the identity scoped to the chosen
 * workspace so the capability list the UI gates on follows the workspace role.
 * Hidden entirely for an account with fewer than two memberships (nothing to
 * switch between) or an external share-link session.
 */
@injectable()
export class WorkspaceSwitcherPresenter {
  @inject(UserModel)
  private userModel!: UserModel

  @inject(AuthenticationRepository)
  private authenticationRepository!: AuthenticationRepository

  @inject(I18nRepository)
  private i18n!: I18nRepository

  constructor() {
    makeObservable(this, { viewModel: computed })
  }

  get viewModel() {
    const memberships = this.userModel.memberships
    const active = this.userModel.activeWorkspace
    return {
      visible: !this.userModel.isExternal && memberships.length > 1,
      label: this.i18n.t('workspace.switcher.label'),
      activeName: active?.name ?? '',
      activeRoleLabel: active ? this.i18n.t(`workspace.role.${active.role}`) : '',
      options: memberships.map((membership) => ({
        workspaceId: membership.workspaceId,
        name: membership.name,
        roleLabel: this.i18n.t(`workspace.role.${membership.role}`),
        isActive: membership.workspaceId === this.userModel.activeWorkspaceId,
      })),
    }
  }

  switchTo = async (workspaceId: string): Promise<void> => {
    if (workspaceId === this.userModel.activeWorkspaceId) return
    await this.authenticationRepository.switchWorkspace(workspaceId)
  }
}
